import { Box, Chip, Stack } from '@mui/material';
import { useCatalogFilters } from '@/packages/courses/context/catalog-filters';
import type { FilterSchemaTagCloud } from '@/packages/courses/types/schema';
import type { CatalogUrlKey } from '@/packages/courses/types/url-params';

interface FilterTagCloudProps extends FilterSchemaTagCloud {
  scrollable?: boolean;
}

export const FilterTagCloud = ({
  filterIdsToDisplay,
  filterIdsToReset,
  scrollable = false,
}: FilterTagCloudProps) => {
  const { params, actions, schema } = useCatalogFilters();

  const chips: { key: CatalogUrlKey; label: string }[] = [];

  for (const filterId of filterIdsToDisplay) {
    const values = params[filterId];
    if (!values?.length) continue;

    const schemaItem = schema.find((s) => 'id' in s && s.id === filterId);
    let label = filterId;

    if (schemaItem && 'options' in schemaItem && schemaItem.options) {
      const labels = values
        .map((v) => schemaItem.options.find((o) => o.value === v)?.label ?? v)
        .filter(Boolean);
      if (labels.length) label = labels.join(', ');
    } else if (schemaItem && 'label' in schemaItem) {
      label = schemaItem.label;
    } else if (filterId === 'terms' && values[0]) {
      label = values[0];
    } else if (filterId === 'location' && values.length) {
      label = values.length > 1 ? `${values[0]} - ${values[1]}` : values[0];
    } else if (filterId === 'sort' && values[0]) {
      const sortItem = schema.find((s) => 'id' in s && s.id === 'sort');
      if (sortItem && 'options' in sortItem) {
        label =
          sortItem.options.find((o) => o.value === values[0])?.label ??
          values[0];
      }
    } else if (values[0]) {
      label = values[0];
    }

    chips.push({ key: filterId, label });
  }

  if (chips.length === 0) return null;

  const handleDelete = (keysToReset: CatalogUrlKey[]) => {
    actions.reset(keysToReset);
  };

  return (
    <Box sx={scrollable ? { overflowX: 'auto', pb: 0.5 } : undefined}>
      <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
        {chips.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            size="small"
            onDelete={() => handleDelete([key])}
          />
        ))}
        <Chip
          label="Limpar filtros"
          size="small"
          variant="outlined"
          onClick={() => handleDelete(filterIdsToReset)}
        />
      </Stack>
    </Box>
  );
};
